import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SearchIcon from "@mui/icons-material/Search";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import axios from "axios";
import { NGROK } from "../../../../APIs";
import TailwindSwitch from "../../../../tailwind-ui/components/TailwindSwitch";
import { awsIcons } from "../AWSIcons";

const BLUE = "#3b83f6";

const Sk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700/60 ${className}`} />
);

const RolesListSkeleton = () => (
  <div className="flex flex-col w-full gap-4 font-sans">
    <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 w-fit">
      <Sk className="h-9 w-16 rounded-lg mx-1" />
      <Sk className="h-9 w-20 rounded-lg mx-1" />
      <Sk className="h-9 w-20 rounded-lg mx-1" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
        <div className="text-white text-center py-3.5 font-semibold tracking-wide" style={{ backgroundColor: BLUE }}>
          Publish Roles
        </div>

        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <Sk className="h-10 w-full rounded-xl" />
        </div>

        <div className="flex-1 overflow-y-auto">
          {Array.from({ length: 10 }).map((_, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sk className="h-9 w-9 rounded-full flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <Sk className="h-4 w-2/3 rounded" />
                  <Sk className="h-3 w-1/3 rounded mt-2" />
                </div>
              </div>
              <Sk className="h-6 w-12 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Sk className="h-10 w-10 rounded-full" />
            <div className="min-w-0 flex-1">
              <Sk className="h-5 w-2/3 rounded" />
              <Sk className="h-4 w-1/3 rounded mt-2" />
            </div>
          </div>
        </div>

        <div className="px-5 pt-4">
          <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700 pb-3">
            <Sk className="h-4 w-16 rounded" />
            <Sk className="h-4 w-24 rounded" />
          </div>
        </div>

        <div className="p-5 flex-1 overflow-auto space-y-5">
          <div>
            <Sk className="h-3 w-24 rounded" />
            <Sk className="h-4 w-2/3 rounded mt-2" />
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <Sk className="h-3 w-20 rounded" />
            <Sk className="h-4 w-full rounded mt-2" />
          </div>
          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <Sk className="h-3 w-24 rounded" />
            <Sk className="h-4 w-full rounded mt-2" />
            <Sk className="h-4 w-5/6 rounded mt-2" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AWSGenericRoles = ({ latestTenant, selectedAwsAccount }) => {
  const [filter, setFilter] = useState("all");
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [policySearch, setPolicySearch] = useState("");
  const [filteredPermission, setFilteredPermission] = useState([]);
  const [filteredusers, setFilteredUsers] = useState([]);
  const [filteredgroups, setFilteredGroups] = useState([]);
  const [modalPermission, setModalPermission] = useState(null);
  const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
  const [policyDetailsTab, setPolicyDetailsTab] = useState("users");
  const [isPermissionLoading, setIsPermissionLoading] = useState(false);

  const getAllUsers = async () => {
    const response = await axios.get(
      `${NGROK}/api/aws/getIamusers?tenantName=${latestTenant}&accountId=${selectedAwsAccount}`
    );
    if (response.status === 200) return response.data;
    else return [];
  };

  const getAllRoles = async () => {
    const response = await axios.get(
      `${NGROK}/api/aws/getIamroles?tenantName=${latestTenant}&accountId=${selectedAwsAccount}`
    );
    if (response.status === 200) return response.data;
    else return [];
  };

  const handleFilterChange = (newFilter) => {
    const filteredRoles = filterRoles(roles, newFilter);
    setFilter(newFilter);
    setSelectedRole(filteredRoles.length > 0 ? filteredRoles[0] : null);
    setSelectedPermission(null);
    setModalPermission(null);
    setSelectedDetailsTab(0);
  };

  const filterRoles = (roles, filter) => {
    if (!roles) return [];
    return roles.filter((role) => {
      const { roleName } = role;
      if (roleName?.startsWith("Whiteswan-Role")) return false;
      if (filter === "default") {
        return roleName?.startsWith("AWSServiceRoleFor");
      } else if (filter === "custom") {
        return !roleName?.startsWith("AWSServiceRoleFor");
      }
      return true;
    });
  };

  const filteredRoles = filterRoles(
    roles?.filter(
      (role) =>
        role?.roleName &&
        role?.roleName.toLowerCase().includes(search.toLowerCase())
    ),
    filter
  );

  const fetchData = async (initialLoad = false) => {
    if (!latestTenant || !selectedAwsAccount) {
      setLoading(false);
      return;
    }
    try {
      const [users, roles] = await Promise.all([getAllUsers(), getAllRoles()]);
      setUsers(users);
      setRoles(roles);
      setLoading(false);
      if (initialLoad) {
        const filteredRoles = filterRoles(roles, filter);
        setSelectedRole(filteredRoles.length > 0 ? filteredRoles[0] : null);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setLoading(false);
    }
  };

  const handleRoleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    // Search is already reactive via state
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setSelectedPermission(null);
    setModalPermission(null);
    setSelectedDetailsTab(0);
  };

  const consolidatedPermissions = selectedRole ? [...selectedRole.rolePolicies] : [];

  const handlePolicySearchChange = (event) => {
    setPolicySearch(event.target.value);
  };

  const handlePermissionSearch = (searchTerm) => {
    const filtered = consolidatedPermissions.filter(
      (permission) =>
        permission?.policyName &&
        permission?.policyName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPermission(filtered);
  };

  const handlePermissionClick = async (permission) => {
    setSelectedPermission(permission.policyARN);
    setIsPermissionLoading(true);
    setPolicyDetailsTab("users");

    const policyArn = permission.policyARN;
    const groupsWithPolicy = users
      .flatMap((user) => user.groups || [])
      .filter((group) =>
        group.groupPolicies?.some((policy) => policy.policyARN === policyArn)
      );
    const usersWithPolicy = users.filter((user) => {
      const hasPolicyInGroups = user.groups?.some((group) =>
        group.groupPolicies?.some((policy) => policy.policyARN === policyArn)
      );
      const hasPolicyInUserPolicies = user.userPolicies?.some(
        (policy) => policy.policyARN === policyArn
      );
      return hasPolicyInGroups || hasPolicyInUserPolicies;
    });

    setFilteredUsers(usersWithPolicy);
    setFilteredGroups(groupsWithPolicy);

    // Get policy document
    const permissionDoc = await getTheSpecificPolicyDocument(permission.policyARN);
    if (permissionDoc) {
      const policyDocumentString = permissionDoc.policyDocument;
      const parsedPolicyDocument = JSON.parse(policyDocumentString);
      permissionDoc.policyDocument = parsedPolicyDocument;
      setModalPermission(permissionDoc);
    } else {
      setModalPermission(null);
    }
    setIsPermissionLoading(false);
  };

  const getTheSpecificPolicyDocument = async (policyArn) => {
    try {
      const response = await axios.get(
        `${NGROK}/api/aws/getSpecificIAMPolicydetails?tenantName=${latestTenant}&policyarn=${encodeURIComponent(policyArn)}&accountId=${selectedAwsAccount}`,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      return response.data;
    } catch (error) {
      return null;
    }
  };

  const updatePermissionARN = async (arn) => {
    const updatedPermissions = filteredPermission.map((permission) => {
      if (permission.policyARN === arn) {
        return {
          ...permission,
          isManagedAttachedPolicies: !permission.isManagedAttachedPolicies,
        };
      }
      return permission;
    });

    try {
      await axios.put(
        `${NGROK}/api/aws/publishPolicies?tenantName=${latestTenant}&policyARN=${arn}&accountId=${selectedAwsAccount}`
      );
      fetchData();
    } catch (err) {
      console.error(err);
    }
    setFilteredPermission(updatedPermissions);
  };

  useEffect(() => {
    fetchData(true);
  }, [selectedAwsAccount]);

  useEffect(() => {
    if (selectedRole) {
      setFilteredPermission(consolidatedPermissions);
    }
  }, [selectedRole]);

  useEffect(() => {
    if (policySearch.length === 0) setFilteredPermission(consolidatedPermissions);
    else handlePermissionSearch(policySearch);
  }, [policySearch]);

  useEffect(() => {
    setLoading(true);
  }, [selectedAwsAccount]);

  const isDefaultRole = (roleName) => roleName?.startsWith("AWSServiceRoleFor");
  const selectedKey = selectedRole?.arn;

  const renderPolicyDetails = () => {
    if (isPermissionLoading) {
      return (
        <div className="flex justify-center py-8">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: BLUE, borderTopColor: "transparent" }}
          />
        </div>
      );
    }

    if (!selectedPermission || !modalPermission) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
          <p className="text-sm mt-2">Select a policy to view details</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Policy Info */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Policy Name
            </label>
            <p className="text-sm text-gray-900 dark:text-white">{modalPermission?.policyName || "—"}</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              ARN
            </label>
            <p className="text-sm text-gray-900 dark:text-white font-mono break-all">{modalPermission?.policyARN || "—"}</p>
          </div>
        </div>

        {/* Sub-tabs for Users/Groups/Permissions */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700 mb-4">
            {[
              { key: "users", label: `Users (${filteredusers?.length || 0})`, icon: PersonIcon },
              { key: "groups", label: `Groups (${filteredgroups?.length || 0})`, icon: GroupIcon },
              { key: "permissions", label: "Permissions", icon: VpnKeyIcon },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setPolicyDetailsTab(tab.key)}
                className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 transition-all ${policyDetailsTab === tab.key
                  ? "border-b-2"
                  : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                style={policyDetailsTab === tab.key ? { color: BLUE, borderColor: BLUE } : undefined}
              >
                <tab.icon sx={{ fontSize: 14 }} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="max-h-48 overflow-y-auto">
            {policyDetailsTab === "users" && (
              filteredusers?.length > 0 ? (
                <div className="space-y-2">
                  {filteredusers.map((user, index) => (
                    <div key={index} className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{user.username}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 break-all">{user.arn}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No users assigned</p>
              )
            )}

            {policyDetailsTab === "groups" && (
              filteredgroups?.length > 0 ? (
                <div className="space-y-2">
                  {filteredgroups.map((group, index) => (
                    <div key={index} className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{group.groupName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 break-all">{group.arn}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No groups assigned</p>
              )
            )}

            {policyDetailsTab === "permissions" && (
              modalPermission?.policyDocument ? (
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-xs font-semibold text-gray-500 uppercase">Version:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{modalPermission.policyDocument.Version}</span>
                  </div>
                  {modalPermission.policyDocument.Statement?.map((stmt, stmtIndex) => (
                    <div key={stmtIndex} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl space-y-2">
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        Statement {stmtIndex + 1}
                      </div>
                      {stmt.Sid && (
                        <div>
                          <span className="text-xs text-gray-500">Sid:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">{stmt.Sid}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-xs text-gray-500">Effect:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{stmt.Effect}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Action:</span>
                        <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                          {Array.isArray(stmt.Action) ? (
                            stmt.Action.map((a, i) => <li key={i} className="break-all">{a}</li>)
                          ) : (
                            <li className="break-all">{stmt.Action}</li>
                          )}
                        </ul>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Resource:</span>
                        <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
                          {Array.isArray(stmt.Resource) ? (
                            stmt.Resource.map((r, i) => <li key={i} className="break-all">{r}</li>)
                          ) : (
                            <li className="break-all">{stmt.Resource}</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No permissions data</p>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <RolesListSkeleton />
      ) : (
        <div className="flex flex-col w-full gap-4 font-sans">
          {/* Filter Tabs */}
          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 w-fit">
            {[
              { key: "all", label: "All" },
              { key: "default", label: "Default" },
              { key: "custom", label: "Custom" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleFilterChange(tab.key)}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${filter === tab.key
                  ? "text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                style={filter === tab.key ? { backgroundColor: BLUE } : undefined}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Roles List */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
              <div
                className="text-white text-center py-3.5 font-semibold tracking-wide"
                style={{ backgroundColor: BLUE }}
              >
                Publish Roles
              </div>

              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={handleRoleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-4 pr-10 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    style={{ boxShadow: "none" }}
                  />
                  <button
                    onClick={handleSearchSubmit}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Search"
                    type="button"
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {filteredRoles?.length > 0 ? (
                  filteredRoles
                    .filter((role) => !role.roleName.startsWith("Whiteswan"))
                    .map((role, index) => {
                      const roleKey = role.arn || index;
                      const isSelected = roleKey === selectedKey;

                      return (
                        <div
                          key={roleKey}
                          onClick={() => handleRoleClick(role)}
                          className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-all duration-150 ${isSelected
                            ? "border-l-4"
                            : "hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-4 border-l-transparent"
                            }`}
                          style={
                            isSelected
                              ? { backgroundColor: "rgba(59,131,246,0.06)", borderLeftColor: BLUE }
                              : undefined
                          }
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div
                              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: "rgba(59,131,246,0.12)" }}
                            >
                              {awsIcons.roles}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm text-gray-700 dark:text-white truncate">
                                {role.roleName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                                {isDefaultRole(role.roleName) ? "AWS Managed Role" : "Custom Role"}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <SecurityIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.25)" }} />
                    <p className="text-sm mt-2">No roles found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Details Panel */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
              {selectedRole ? (
                <>
                  {/* Role Header */}
                  <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "rgba(59,131,246,0.12)" }}
                      >
                        {awsIcons.roles}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-500 dark:text-white truncate">
                          {selectedRole?.roleName}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {isDefaultRole(selectedRole?.roleName) ? "AWS Managed Role" : "Custom Role"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="px-5 pt-4">
                    <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setSelectedDetailsTab(0)}
                        className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-all ${selectedDetailsTab === 0
                          ? "border-b-2"
                          : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                          }`}
                        style={selectedDetailsTab === 0 ? { color: BLUE, borderColor: BLUE } : undefined}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                        Info
                      </button>
                      <button
                        onClick={() => setSelectedDetailsTab(1)}
                        className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-all ${selectedDetailsTab === 1
                          ? "border-b-2"
                          : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                          }`}
                        style={selectedDetailsTab === 1 ? { color: BLUE, borderColor: BLUE } : undefined}
                      >
                        <VpnKeyIcon fontSize="small" />
                        Policies
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-5 flex-1 overflow-auto">
                    {selectedDetailsTab === 0 && (
                      <div className="space-y-5">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role Name
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedRole?.roleName || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role ARN
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white font-mono break-all">
                            {selectedRole?.arn || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Attached Policies
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedRole?.rolePolicies?.length || 0} policies
                          </p>
                        </div>
                      </div>
                    )}

                    {selectedDetailsTab === 1 && (
                      <div className="space-y-4">
                        {/* Policy Search */}
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search policies..."
                            value={policySearch}
                            onChange={handlePolicySearchChange}
                            className="w-full pl-4 pr-10 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                          />
                          <SearchIcon
                            fontSize="small"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                          />
                        </div>

                        {/* Policies List */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Attached Policies
                          </span>
                          <span
                            className="px-2 py-0.5 text-xs font-semibold rounded-full"
                            style={{ backgroundColor: "rgba(59,131,246,0.12)", color: BLUE }}
                          >
                            {filteredPermission?.length || 0}
                          </span>
                        </div>

                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {filteredPermission?.length > 0 ? (
                            filteredPermission.map((permission, idx) => (
                              <div
                                key={idx}
                                onClick={() => handlePermissionClick(permission)}
                                className={`flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-all ${selectedPermission === permission.policyARN
                                  ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                                  : "bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800"
                                  }`}
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <div
                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: BLUE }}
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                    {permission?.policyName}
                                  </span>
                                </div>
                                <div onClick={(e) => e.stopPropagation()}>
                                  <TailwindSwitch
                                    isDisabled={false}
                                    data={permission}
                                    isChecked={permission.isManagedAttachedPolicies}
                                    action={(e, data) => updatePermissionARN(data?.policyARN)}
                                  />
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                              <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
                              <p className="text-sm mt-2">No policies attached</p>
                            </div>
                          )}
                        </div>

                        {/* Policy Details Section */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 block">
                            Policy Details
                          </span>
                          {renderPolicyDetails()}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <SecurityIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />
                  <p className="text-sm mt-2">Select a role to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AWSGenericRoles;
